import { ObjectConfig, required } from "@homebound/form-state";
import { ChangeEventWorkflow, SaveChangeEventMaterialVariantSwapInput } from "src/generated/graphqlTypes";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { reviewImpactStep, reviewStep } from "src/routes/projects/change-events/guided/workflows/sharedSteps";
import { SelectProductToReplaceStep } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/1.SelectProductToReplaceStep";
import { defineWorkflow } from "src/routes/projects/change-events/guided/workflows/types";
import { useSwapProductInCatalogActions } from "./useSwapProductInCatalogActions";

export type SwapProductInCatalogFormInput = { swaps: SaveChangeEventMaterialVariantSwapInput[] };

// Exported so tests can build their form state from the same config the runner uses.
export const swapProductInCatalogFormConfig: ObjectConfig<SwapProductInCatalogFormInput> = {
  swaps: {
    type: "list",
    // At least one swap is needed to create the change event. The list is exactly what gets sent,
    // so removing a swap removes its row and an empty list means there is nothing to save.
    rules: [({ value }) => (value.nonEmpty ? undefined : "At least one product swap is required")],
    config: {
      id: { type: "value" },
      proposedMaterialVariantId: { type: "value", rules: [required] },
      originalProjectItemId: { type: "value", rules: [required] },
    },
  },
};

export const swapProductInCatalogWorkflow = defineWorkflow<SwapProductInCatalogFormInput>({
  workflow: ChangeEventWorkflow.SwapProductInCatalog,
  formConfig: swapProductInCatalogFormConfig,
  // In edit mode, map the saved swaps back to the form state
  mapToForm: (changeEvent) => ({
    swaps: changeEvent.materialVariantSwaps.map((swap) => ({
      id: swap.id,
      originalProjectItemId: swap.originalProjectItem.id,
      proposedMaterialVariantId: swap.proposedMaterialVariant.id,
    })),
  }),
  steps: [
    {
      value: GuidedChangeEventStep.SelectProductToReplace,
      label: "Select Product to Replace",
      isComplete: (form) => form.valid,
      render: (form) => <SelectProductToReplaceStep formState={form} />,
    },
    reviewImpactStep(),
    reviewStep(),
  ],
  useActions: (args) => {
    return { onSubmit: async () => {}, onContinue: { ...useSwapProductInCatalogActions(args) } };
  },
});
